import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';

/**
 * Generic reusable dialog component
 */
export function GenericMessageDialog({
    open,
    onClose,
    header,
    message,
    type,
    rejectCallback,
    acceptCallback,
    yesText = 'Yes',
    noText = 'No',
    okText = 'OK',
    closeText = 'Close',
    size = 'sm',         // Default dialog size (Material UI maxWidth)
    maxWidthPx = null,   // New prop to control exact max width in pixels
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
            maxWidth={maxWidthPx ? false : size} // Disable default size if custom width is set
            fullWidth
            PaperProps={{
                style: maxWidthPx
                    ? { maxWidth: `${maxWidthPx}px` }
                    : size === 'sm'
                        ? { maxWidth: 400 } // Adjust default sm size
                        : {},
            }}
        >
            {header && <DialogTitle id="dialog-title">{header}</DialogTitle>}

            <DialogContent>
                {typeof message === 'string' ? (
                    <DialogContentText id="dialog-description">{message}</DialogContentText>
                ) : (
                    message
                )}
            </DialogContent>

            <DialogActions>
                {type === 'confirm' && (
                    <>
                        <Button
                            onClick={() => {
                                if (acceptCallback) acceptCallback();
                                if (onClose) onClose();
                            }}
                            color="primary"
                        >
                            {yesText}
                        </Button>
                        <Button
                            onClick={() => {
                                if (rejectCallback) rejectCallback();
                                if (onClose) onClose();
                            }}
                            color="primary"
                            autoFocus
                        >
                            {noText}
                        </Button>
                    </>
                )}

                {type === 'info' && (
                    <Button
                        onClick={() => {
                            if (acceptCallback) acceptCallback();
                            if (onClose) onClose();
                        }}
                        color="primary"
                        autoFocus
                    >
                        {okText}
                    </Button>
                )}

                {type === 'error' && (
                    <Button
                        onClick={() => {
                            if (onClose) onClose();
                        }}
                        color="error"
                        autoFocus
                    >
                        {closeText}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

GenericMessageDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    header: PropTypes.string,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    type: PropTypes.oneOf(['info', 'confirm', 'error']).isRequired,
    rejectCallback: PropTypes.func,
    acceptCallback: PropTypes.func,
    yesText: PropTypes.string,
    noText: PropTypes.string,
    okText: PropTypes.string,
    closeText: PropTypes.string,
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    maxWidthPx: PropTypes.number, // New prop
};
